import { ChevronRight, Plus, Trash2, UserPlus } from "lucide-react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { formatDate } from "@/components/deal-metrics/shared";
import { EmptyState } from "@/components/shared/empty-state";
import { PageHeader } from "@/components/shared/page-header";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { CandidateCreateDialog } from "./recruitment/candidate-create-dialog";
import { PositionCreateDialog } from "./recruitment/position-create-dialog";
import {
  type Candidate,
  type Position,
  PRIORITY_BADGE_CLASS,
  PRIORITY_OPTIONS,
  priorityLabel,
  STAGE_BADGE_CLASS,
  STAGE_OPTIONS,
  STATUS_BADGE_CLASS,
  STATUS_OPTIONS,
  stageLabel,
  statusLabel,
} from "./recruitment/shared";

interface Stats {
  openRoles: number;
  candidatesInProcess: number;
  offersOutstanding: number;
}

function StatCard({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="rounded-md border p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold tabular-nums mt-1">{value ?? "—"}</p>
    </div>
  );
}

export function HRRecruitmentPage() {
  const [positions, setPositions] = useState<Position[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [candidatesByPosition, setCandidatesByPosition] = useState<Map<string, Candidate[]>>(
    new Map(),
  );
  const [loadingCandidates, setLoadingCandidates] = useState<Set<string>>(new Set());
  const [showCreatePosition, setShowCreatePosition] = useState(false);
  const [addCandidateFor, setAddCandidateFor] = useState<Position | null>(null);

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/recruitment/positions");
      const data = res.ok ? await res.json() : { positions: [] };
      setPositions(data.positions ?? []);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    const res = await fetch("/api/recruitment/stats");
    if (res.ok) setStats(await res.json());
  }, []);

  useEffect(() => {
    void fetchPositions();
    void fetchStats();
  }, [fetchPositions, fetchStats]);

  const fetchCandidates = useCallback(async (positionId: string) => {
    setLoadingCandidates((prev) => new Set(prev).add(positionId));
    try {
      const res = await fetch(`/api/recruitment/positions/${positionId}/candidates`);
      const data = res.ok ? await res.json() : { candidates: [] };
      setCandidatesByPosition((prev) => new Map(prev).set(positionId, data.candidates ?? []));
    } finally {
      setLoadingCandidates((prev) => {
        const next = new Set(prev);
        next.delete(positionId);
        return next;
      });
    }
  }, []);

  const toggleExpand = (positionId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(positionId)) {
        next.delete(positionId);
      } else {
        next.add(positionId);
        if (!candidatesByPosition.has(positionId)) void fetchCandidates(positionId);
      }
      return next;
    });
  };

  const refreshAfterChange = () => {
    void fetchPositions();
    void fetchStats();
  };

  const updatePositionField = async (
    positionId: string,
    field: "status" | "priority",
    value: string,
  ) => {
    const res = await fetch(`/api/recruitment/positions/${positionId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [field]: value }),
    });
    if (!res.ok) {
      toast.error("Failed to update position");
      return;
    }
    refreshAfterChange();
  };

  const deletePosition = async (positionId: string) => {
    const res = await fetch(`/api/recruitment/positions/${positionId}`, { method: "DELETE" });
    if (!res.ok) {
      toast.error("Failed to delete position");
      return;
    }
    toast.success("Position deleted");
    refreshAfterChange();
  };

  const updateCandidateStage = async (positionId: string, candidateId: string, stage: string) => {
    const res = await fetch(`/api/recruitment/candidates/${candidateId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stage }),
    });
    if (!res.ok) {
      toast.error("Failed to update candidate");
      return;
    }
    void fetchCandidates(positionId);
    void fetchStats();
  };

  const deleteCandidate = async (positionId: string, candidateId: string) => {
    const res = await fetch(`/api/recruitment/candidates/${candidateId}`, { method: "DELETE" });
    if (!res.ok) {
      toast.error("Failed to remove candidate");
      return;
    }
    toast.success("Candidate removed");
    void fetchCandidates(positionId);
    refreshAfterChange();
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title="Recruitment"
        description="Open roles and candidates moving through the pipeline."
        actions={
          <Button onClick={() => setShowCreatePosition(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Position
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard label="Open Roles" value={stats?.openRoles ?? null} />
        <StatCard label="Candidates In Process" value={stats?.candidatesInProcess ?? null} />
        <StatCard label="Offers Outstanding" value={stats?.offersOutstanding ?? null} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Open Positions</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 3 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholders have no stable ID
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : positions.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={UserPlus}
                heading="No open positions yet"
                description="Add a position to start tracking candidates for it."
                actionLabel="Add Position"
                onAction={() => setShowCreatePosition(true)}
              />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Position</TableHead>
                  <TableHead>Team</TableHead>
                  <TableHead>Hiring Manager</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Candidates</TableHead>
                  <TableHead>Date Opened</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {positions.map((position) => {
                  const isExpanded = expanded.has(position.id);
                  const candidates = candidatesByPosition.get(position.id) ?? [];
                  const isLoadingCandidates = loadingCandidates.has(position.id);
                  return (
                    <Fragment key={position.id}>
                      <TableRow
                        className="cursor-pointer select-none hover:bg-muted/20"
                        role="button"
                        tabIndex={0}
                        onClick={() => toggleExpand(position.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleExpand(position.id);
                          }
                        }}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-1.5">
                            <ChevronRight
                              className={cn(
                                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                                isExpanded && "rotate-90",
                              )}
                            />
                            {position.title}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {position.team ?? "—"}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {position.hiringManagerName ?? "—"}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={position.priority}
                            onValueChange={(v) => updatePositionField(position.id, "priority", v)}
                          >
                            <SelectTrigger className="h-8 w-28 text-xs" size="sm">
                              <Badge
                                className={cn(
                                  "font-medium",
                                  PRIORITY_BADGE_CLASS[position.priority],
                                )}
                              >
                                {priorityLabel(position.priority)}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {PRIORITY_OPTIONS.map((p) => (
                                <SelectItem key={p.value} value={p.value}>
                                  {p.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={position.status}
                            onValueChange={(v) => updatePositionField(position.id, "status", v)}
                          >
                            <SelectTrigger className="h-8 w-28 text-xs" size="sm">
                              <Badge
                                className={cn("font-medium", STATUS_BADGE_CLASS[position.status])}
                              >
                                {statusLabel(position.status)}
                              </Badge>
                            </SelectTrigger>
                            <SelectContent>
                              {STATUS_OPTIONS.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center tabular-nums">
                          {position.candidateCount}
                        </TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">
                          {formatDate(position.dateOpened)}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete {position.title}?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This removes the position and all {position.candidateCount}{" "}
                                  candidate{position.candidateCount === 1 ? "" : "s"} tracked
                                  against it. This can't be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => deletePosition(position.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow className="bg-muted/10 hover:bg-muted/10">
                          <TableCell colSpan={8} className="p-0">
                            <div className="p-4 pl-10 space-y-3">
                              <div className="flex items-center justify-between">
                                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                  Candidates
                                </h4>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setAddCandidateFor(position)}
                                >
                                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                                  Add Candidate
                                </Button>
                              </div>
                              {isLoadingCandidates ? (
                                <Skeleton className="h-16 w-full" />
                              ) : candidates.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-2">
                                  No candidates yet for this position.
                                </p>
                              ) : (
                                <div className="rounded-md border bg-background">
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Contact</TableHead>
                                        <TableHead>Source</TableHead>
                                        <TableHead>Stage</TableHead>
                                        <TableHead>Notes</TableHead>
                                        <TableHead className="w-10" />
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {candidates.map((candidate) => (
                                        <TableRow key={candidate.id}>
                                          <TableCell className="font-medium">
                                            {candidate.name}
                                          </TableCell>
                                          <TableCell className="text-xs text-muted-foreground">
                                            {candidate.email && <div>{candidate.email}</div>}
                                            {candidate.phone && <div>{candidate.phone}</div>}
                                            {!candidate.email && !candidate.phone && "—"}
                                            {candidate.resumeUrl && (
                                              <a
                                                href={candidate.resumeUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-primary hover:underline"
                                              >
                                                Resume
                                              </a>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-sm text-muted-foreground">
                                            {candidate.source ?? "—"}
                                          </TableCell>
                                          <TableCell>
                                            <Select
                                              value={candidate.stage}
                                              onValueChange={(v) =>
                                                updateCandidateStage(position.id, candidate.id, v)
                                              }
                                            >
                                              <SelectTrigger className="h-8 w-32 text-xs" size="sm">
                                                <Badge
                                                  className={cn(
                                                    "font-medium",
                                                    STAGE_BADGE_CLASS[candidate.stage],
                                                  )}
                                                >
                                                  {stageLabel(candidate.stage)}
                                                </Badge>
                                              </SelectTrigger>
                                              <SelectContent>
                                                {STAGE_OPTIONS.map((s) => (
                                                  <SelectItem key={s.value} value={s.value}>
                                                    {s.label}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </TableCell>
                                          <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                                            {candidate.notes ?? "—"}
                                          </TableCell>
                                          <TableCell>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                                              onClick={() =>
                                                deleteCandidate(position.id, candidate.id)
                                              }
                                            >
                                              <Trash2 className="h-3.5 w-3.5" />
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PositionCreateDialog
        open={showCreatePosition}
        onOpenChange={setShowCreatePosition}
        onCreated={refreshAfterChange}
      />

      {addCandidateFor && (
        <CandidateCreateDialog
          positionId={addCandidateFor.id}
          positionTitle={addCandidateFor.title}
          open={!!addCandidateFor}
          onOpenChange={(open) => {
            if (!open) setAddCandidateFor(null);
          }}
          onCreated={() => {
            void fetchCandidates(addCandidateFor.id);
            refreshAfterChange();
          }}
        />
      )}
    </div>
  );
}
